import React, { useState, useEffect } from 'react';
import { useHistory, useLocation, Link } from 'react-router-dom';
import { FaProjectDiagram, FaBoxOpen } from 'react-icons/fa';
import { PropagateLoader } from 'react-spinners';

import BreadCrumb from '~/components/BreadCrumb';
import DefaultInput from '~/components/Inputs/Default';
import NewButton from '~/components/Buttons/New';
import Pagination from '~/components/Pagination';
import SelectButton from '~/components/Buttons/Select';

import { getFormattedCnpj } from '~/utils';

import { useAuth } from '~/hooks/auth';

import api from '~/services/api';

import { Container, Table, Td } from './styles';

const AMOUNT_PER_PAGE = 5;

const ListClients: React.FC = () => {
  const history = useHistory();
  const { user } = useAuth();
  const { state } = useLocation<{ code: string; name: string }>();
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(0);
  const [filter, setFilter] = useState('');
  const [clients, setClients] = useState<
    {
      code: string;
      name: string;
      company_name: string;
      document: string;
      address: string;
      city: string;
      state: string;
    }[]
  >([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const checkEnabledFunctionality = async () => {
      await api.get('/generalsettings').then(response => {
        if (response.data && !response.data.new_sample_req_enabled) {
          history.push('/');
        }
      });
    };

    checkEnabledFunctionality();
  }, [history]);

  useEffect(() => {
    const loadClients = async () => {
      let erpRepCode = '';

      if (state !== undefined) {
        erpRepCode = state.code;
      } else if (user.user_type.type === 'REP') {
        erpRepCode = user.erp_rep_code;
      }

      await api
        .get(
          `/clients/paginate?` +
            `page=${page}&` +
            `filter=${filter}&` +
            `user_type=${user.user_type.type}&` +
            `erp_rep_code=${erpRepCode}&` +
            `amount=${AMOUNT_PER_PAGE}`,
        )
        .then(response => {
          setClients(response.data[0]);
          setCount(response.data[1]);
          setLoading(false);
        });
    };

    loadClients();
  }, [page, filter, state, user]);

  return (
    <Container style={{ minHeight: 600 }}>
      <BreadCrumb
        main="Solicitações"
        path="/projects/main"
        firstChildPath="/projects/samples"
        firstChild="Amostras"
        secondChild="Nova Solicitação"
        mainIcon={FaProjectDiagram}
        firstChildIcon={FaBoxOpen}
        secondChildIcon={FaBoxOpen}
        mainColored
        firstChildColored
      />

      <div style={{ width: '100%', maxWidth: 512 }}>
        <DefaultInput
          name="search"
          labelText="Pesquisar"
          type="text"
          onChange={event => {
            setFilter(event.target.value);
            setPage(0);
          }}
        />
      </div>

      <Table>
        <thead>
          <tr>
            <th style={{ width: '10%', textAlign: 'left' }}>Código</th>
            <th style={{ width: '20%', textAlign: 'left' }}>Nome Fantasia</th>
            <th style={{ width: '25%', textAlign: 'left' }}>Razão Social</th>
            <th style={{ width: '15%', textAlign: 'left' }}>CNPJ</th>
            <th style={{ width: '15%', textAlign: 'left' }}>Cidade</th>
            <th style={{ width: '10%', textAlign: 'left' }}>Estado</th>
            <th style={{ width: '5%', textAlign: 'center' }} />
          </tr>
        </thead>

        <tbody>
          {loading ? (
            <tr>
              <Td colSpan={7} style={{ padding: '3rem', textAlign: 'center' }}>
                <PropagateLoader color="#276c8d" />
              </Td>
            </tr>
          ) : clients.length > 0 ? (
            clients.map(client => (
              <tr key={client.code}>
                <Td
                  style={{ width: '10%', textAlign: 'left' }}
                  heading="CÓDIGO"
                >
                  {client.code}
                </Td>
                <Td
                  style={{ width: '20%', textAlign: 'left' }}
                  heading="NOME FANTASIA"
                >
                  {client.name}
                </Td>
                <Td
                  style={{ width: '25%', textAlign: 'left' }}
                  heading="RAZÃO SOCIAL"
                >
                  {client.company_name}
                </Td>
                <Td style={{ width: '15%', textAlign: 'left' }} heading="CNPJ">
                  {getFormattedCnpj(client.document)}
                </Td>
                <Td
                  style={{ width: '15%', textAlign: 'left' }}
                  heading="CIDADE"
                >
                  {client.city}
                </Td>
                <Td
                  style={{ width: '10%', textAlign: 'left' }}
                  heading="ESTADO"
                >
                  {client.state}
                </Td>
                <Td
                  style={{
                    width: '5%',
                    display: 'flex',
                    textAlign: 'center',
                  }}
                  heading={' '}
                >
                  <Link
                    to={{
                      pathname: `/projects/samples/procedure1/new`,
                      state: {
                        erp_client_code: client.code,
                        erp_client_name: client.name,
                        erp_client_company_name: client.company_name,
                        erp_client_document: client.document,
                        erp_client_address: client.address,
                        erp_client_city: client.city,
                        erp_client_state: client.state,
                        erp_rep_code:
                          state !== undefined ? state.code : user.erp_rep_code,
                        rep_as_client: false,
                      },
                    }}
                  >
                    <SelectButton />
                  </Link>
                </Td>
              </tr>
            ))
          ) : (
            <tr>
              <Td colSpan={7} style={{ textAlign: 'center' }}>
                Nenhuma informação disponível
              </Td>
            </tr>
          )}
        </tbody>
      </Table>

      <div style={{ marginTop: '2rem', display: 'flex', gap: '0.5rem' }}>
        <Pagination
          page={page}
          count={count}
          amountPerPage={AMOUNT_PER_PAGE}
          setPage={setPage}
        />

        <Link
          to={{
            pathname: `/projects/samples/procedure1/new`,
            state: {
              erp_rep_code:
                state !== undefined ? state.code : user.erp_rep_code,
              rep_as_client: true,
            },
          }}
        >
          <NewButton
            label="Solicitação Representante"
            style={{ padding: '1.2rem 2rem', fontSize: '1.4rem' }}
          />
        </Link>
      </div>
    </Container>
  );
};

export default ListClients;
